import type { NormalizedRect } from "@desk/core";

/** Minimal frame shape shared by preview CSS and canvas export. */
export interface FrameOpLike {
  crop: NormalizedRect;
  zoomMilli: number;
}

/** Canvas `drawImage` source/dest rectangles in pixels. */
export interface DrawImageGeometry {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
  dx: number;
  dy: number;
  dw: number;
  dh: number;
}

/** Absolute video placement inside an overflow:hidden host matching DrawImageGeometry. */
export interface CssVideoCrop {
  widthPx: number;
  heightPx: number;
  leftPx: number;
  topPx: number;
}

/**
 * Map optional frame crop/zoom (millipoints + zoomMilli) into drawImage geometry.
 * Crop is authored camera space; zoom scales into the crop center. Dest fills the canvas.
 */
export function computeFrameDrawGeometry(
  frame: FrameOpLike | null | undefined,
  videoWidth: number,
  videoHeight: number,
  canvasWidth: number,
  canvasHeight: number,
): DrawImageGeometry {
  const vw = Math.max(1, videoWidth);
  const vh = Math.max(1, videoHeight);
  const cw = Math.max(1, canvasWidth);
  const ch = Math.max(1, canvasHeight);

  let cropX = 0;
  let cropY = 0;
  let cropW = vw;
  let cropH = vh;
  let zoom = 1;
  if (frame) {
    cropX = (frame.crop.xMilli / 1000) * vw;
    cropY = (frame.crop.yMilli / 1000) * vh;
    cropW = Math.max(1, (frame.crop.wMilli / 1000) * vw);
    cropH = Math.max(1, (frame.crop.hMilli / 1000) * vh);
    zoom = Math.max(0.1, frame.zoomMilli / 1000);
  }

  let sw = cropW / zoom;
  let sh = cropH / zoom;
  let sx = cropX + cropW / 2 - sw / 2;
  let sy = cropY + cropH / 2 - sh / 2;

  if (sx < 0) sx = 0;
  if (sy < 0) sy = 0;
  if (sx + sw > vw) sx = Math.max(0, vw - sw);
  if (sy + sh > vh) sy = Math.max(0, vh - sh);
  sw = Math.max(1, Math.min(sw, vw - sx));
  sh = Math.max(1, Math.min(sh, vh - sy));

  return { sx, sy, sw, sh, dx: 0, dy: 0, dw: cw, dh: ch };
}

/** CSS placement equivalent to `ctx.drawImage(video, sx,sy,sw,sh, dx,dy,dw,dh)`. */
export function geometryToCssVideoCrop(
  geom: DrawImageGeometry,
  videoWidth: number,
  videoHeight: number,
): CssVideoCrop {
  const scaleX = geom.dw / geom.sw;
  const scaleY = geom.dh / geom.sh;
  return {
    widthPx: videoWidth * scaleX,
    heightPx: videoHeight * scaleY,
    leftPx: -geom.sx * scaleX + geom.dx,
    topPx: -geom.sy * scaleY + geom.dy,
  };
}

export function applyCssVideoCrop(video: HTMLElement, crop: CssVideoCrop): void {
  video.style.position = "absolute";
  video.style.margin = "0";
  video.style.objectFit = "fill";
  video.style.transform = "none";
  video.style.objectPosition = "0 0";
  video.style.width = `${crop.widthPx}px`;
  video.style.height = `${crop.heightPx}px`;
  video.style.left = `${crop.leftPx}px`;
  video.style.top = `${crop.topPx}px`;
}

export function drawVideoWithGeometry(
  ctx: CanvasRenderingContext2D,
  video: CanvasImageSource,
  geom: DrawImageGeometry,
): void {
  ctx.drawImage(
    video,
    geom.sx,
    geom.sy,
    geom.sw,
    geom.sh,
    geom.dx,
    geom.dy,
    geom.dw,
    geom.dh,
  );
}
